/**
 * Standard pill button used by every action surface (rotate / escalate /
 * trigger / clear / etc.). Variants: primary | secondary | danger | ghost.
 */

import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { OcppTheme } from './OcppTheme';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface OcppActionButtonProps {
  label: string;
  onPress: () => void;
  variant?: Variant;
  disabled?: boolean;
  loading?: boolean;
  icon?: keyof typeof MaterialIcons.glyphMap;
  style?: ViewStyle;
  accessibilityLabel?: string;
}

const stylesForVariant = (
  variant: Variant,
  disabled: boolean
): { background: string; border: string; text: string } => {
  const dim = disabled ? 0.5 : 1;
  const withAlpha = (hex: string) => hex; // hook in case we add opacity later
  switch (variant) {
    case 'primary':
      return {
        background: disabled ? OcppTheme.accentDim : OcppTheme.accent,
        border: OcppTheme.accent,
        text: '#fff',
      };
    case 'danger':
      return {
        background: disabled ? `${OcppTheme.danger}33` : OcppTheme.danger,
        border: OcppTheme.danger,
        text: '#fff',
      };
    case 'ghost':
      return {
        background: 'transparent',
        border: OcppTheme.textMuted,
        text: withAlpha(OcppTheme.textSecondary),
      };
    case 'secondary':
    default:
      return {
        background: 'transparent',
        border: OcppTheme.accent,
        text: OcppTheme.accent,
      };
  }
  void dim;
};

const OcppActionButton: React.FC<OcppActionButtonProps> = ({
  label,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  icon,
  style,
  accessibilityLabel,
}) => {
  const { background, border, text } = stylesForVariant(variant, disabled || loading);
  const isInert = disabled || loading;

  return (
    <Pressable
      onPress={onPress}
      disabled={isInert}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInert, busy: loading }}
      accessibilityLabel={accessibilityLabel ?? label}
      style={({ pressed }) => [
        styles.button,
        {
          backgroundColor: background,
          borderColor: border,
          opacity: isInert ? 0.6 : pressed ? 0.85 : 1,
        },
        style,
      ]}
    >
      <View style={styles.row}>
        {loading ? (
          <ActivityIndicator size="small" color={text} />
        ) : icon ? (
          <MaterialIcons name={icon} size={16} color={text} />
        ) : null}
        <Text style={[styles.label, { color: text }]}>{label}</Text>
      </View>
    </Pressable>
  );
};

export default OcppActionButton;

const styles = StyleSheet.create({
  button: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1,
    minHeight: 38,
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
  },
});
